import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import { FormikProps, withFormik } from 'formik';
import * as yup from 'yup';
import * as voicifyApi from '../../../api';
import TextField from '../../../components/forms/TextField';
import Button from '../../../components/general/Button';
import FormFieldsContainer from '../../../components/structure/FormFieldsContainer';
import { SENTIMENT_OPTIONS } from '../../../constants/sentimentOptions';
import { color_shades_dark, color_shades_darker, color_colors_growth } from '../../../constants/colors';
import IconTooltip from '../../../components/general/IconTooltip';
import CreateTagRequest from '../../../models/configuration/CreateTagRequest';

interface EditTagFormProps {
    tagName: string
    featureFlagId: string
    initialDisplayName: string
    initialSentiment: string
    onSuccess: () => void
    onCancel: () => void
}

interface EditTagFormData {
    tagDisplayName: string
    sentiment: string
    detectionPrompt: string
    summarizationPromptAddition: string
    autoTagFlags: string
    detectionTolerance: string
}

interface EditTagFormState {
    submitResult: 'success' | 'error' | null
    submitMessage: string
}

interface BackendConfig {
    detectionPrompt: string
    summarizationPromptAddition: string
    autoTagFlags: string
    detectionTolerance: string
}

const InnerForm: React.FC<FormikProps<EditTagFormData> & EditTagFormProps & EditTagFormState & { setSubmitState: (state: EditTagFormState) => void }> = (props) => {
    return (
        <form onSubmit={props.handleSubmit} className={formStyle}>
            <FormFieldsContainer>
                <div className={readOnlyFieldStyle}>
                    <div className={labelWithTooltipStyle}>
                        <label className={labelStyle}>Tag Name</label>
                        <IconTooltip text="The unique identifier for this tag. This field cannot be changed after creation." place="right" />
                    </div>
                    <div className={readOnlyValueStyle}>{props.tagName}</div>
                </div>

                <div className={readOnlyFieldStyle}>
                    <div className={labelWithTooltipStyle}>
                        <label className={labelStyle}>Feature Flag</label>
                        <IconTooltip text="The vertical this tag belongs to. This field cannot be changed after creation." place="right" />
                    </div>
                    <div className={readOnlyValueStyle}>{props.featureFlagId}</div>
                </div>

                <TextField
                    name="tagDisplayName"
                    value={props.values.tagDisplayName}
                    label="Tag Display Name *"
                    tooltip="The human-readable name shown in the analytics UI (e.g. Upsell Successful)."
                    placeholder="e.g. Upsell Successful"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                />

                <div className={fieldGroupStyle}>
                    <div className={labelWithTooltipStyle}>
                        <label className={labelStyle}>Sentiment *</label>
                        <IconTooltip text="This defines how we color code the tag when displaying (positive = green, negative = red, caution = yellow, neutral = blue). Ex: ACTION_COMPLETED is a positive and thus a green tag" place="right" />
                    </div>
                    <select
                        name="sentiment"
                        value={props.values.sentiment}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        className={selectStyle}
                    >
                        {SENTIMENT_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {props.touched.sentiment && props.errors.sentiment && (
                        <div className={errorStyle}>{props.errors.sentiment}</div>
                    )}
                </div>

                <div className={fieldGroupStyle}>
                    <div className={labelWithTooltipStyle}>
                        <label className={labelStyle}>Detection Prompt</label>
                        <IconTooltip text="This is a prompt for configuring a generative tag, if you don't know what that means leave this blank. If you are using this to make a generative tag be sure the team (mainly Jack) is aware" place="right" />
                    </div>
                    <textarea
                        name="detectionPrompt"
                        value={props.values.detectionPrompt}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        className={textareaStyle}
                        placeholder="The AI prompt used to detect this tag in conversations"
                        rows={4}
                    />
                </div>

                <div className={fieldGroupStyle}>
                    <div className={labelWithTooltipStyle}>
                        <label className={labelStyle}>Summarization Prompt Addition</label>
                        <IconTooltip text="This is a prompt modification to the summary assuming this tag was detected. These get appeneded to 'Your summary should...'. Ex: If the tag was UNSUPPORTED_APPOINTMENT_TYPE you may want to put here 'Include the unsupported appointment type the customer requested'" place="right" />
                    </div>
                    <textarea
                        name="summarizationPromptAddition"
                        value={props.values.summarizationPromptAddition}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        className={textareaStyle}
                        placeholder="Additional prompt text appended during summarization"
                        rows={3}
                    />
                </div>

                <TextField
                    name="autoTagFlags"
                    value={props.values.autoTagFlags}
                    label="Auto Tag Flags"
                    tooltip="Comma-separated flags that trigger automatic tagging (e.g. UPSOLD, upsell-offered)."
                    placeholder="Comma-separated flags, e.g. UPSOLD, upsell-offered"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                />

                <TextField
                    name="detectionTolerance"
                    value={props.values.detectionTolerance}
                    label="Detection Tolerance"
                    tooltip="A value between 0.0 and 1.0 that controls how strictly the tag is detected. ONLY SHOULD BE USING IF YOU ARE MAKING A GENERATIVE TAG"
                    placeholder="0.0 - 1.0"
                    type="number"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                />
            </FormFieldsContainer>

            {props.submitResult === 'success' && (
                <div className={successMessageStyle}>{props.submitMessage}</div>
            )}
            {props.submitResult === 'error' && (
                <div className={errorMessageStyle}>{props.submitMessage}</div>
            )}

            <div className={buttonContainerStyle}>
                <Button
                    themes={['white', 'secondary']}
                    text="Cancel"
                    type="button"
                    onClick={props.onCancel}
                />
                <Button
                    themes={['primary']}
                    text="Save Changes"
                    type="submit"
                    loading={props.isSubmitting}
                    disabled={props.isSubmitting}
                />
            </div>
        </form>
    );
};

const EditTagFormWrapper: React.FC<EditTagFormProps> = (props) => {
    const [submitState, setSubmitState] = useState<EditTagFormState>({
        submitResult: null,
        submitMessage: ''
    });
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [backendConfig, setBackendConfig] = useState<BackendConfig>({
        detectionPrompt: '',
        summarizationPromptAddition: '',
        autoTagFlags: '',
        detectionTolerance: ''
    });

    useEffect(() => {
        let cancelled = false;
        const fetchConfig = async () => {
            try {
                const result = await voicifyApi.getTagConfig(props.tagName, props.featureFlagId);
                if (cancelled) return;
                if (result.resultType === 'Ok' && result.data) {
                    const autoFlagsStr = result.data.autoTagFlags
                        ? result.data.autoTagFlags.join(', ')
                        : '';
                    const toleranceStr = result.data.detectionTolerance
                        ? String(result.data.detectionTolerance)
                        : '';
                    setBackendConfig({
                        detectionPrompt: result.data.detectionPrompt || '',
                        summarizationPromptAddition: result.data.summarizationPromptAddition || '',
                        autoTagFlags: autoFlagsStr,
                        detectionTolerance: toleranceStr
                    });
                } else {
                    setLoadError('Could not load existing tag configuration. Fields will start empty.');
                }
            } catch {
                if (!cancelled) {
                    setLoadError('Could not load existing tag configuration. Fields will start empty.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        fetchConfig();
        return () => { cancelled = true; };
    }, [props.tagName, props.featureFlagId]);

    if (loading) {
        return <div className={loadingStyle}>Loading tag configuration...</div>;
    }

    const FormWithFormik = withFormik<EditTagFormProps & EditTagFormState & { setSubmitState: (state: EditTagFormState) => void } & { backendConfig: BackendConfig }, EditTagFormData>({
        mapPropsToValues: (formProps) => ({
            tagDisplayName: formProps.initialDisplayName || '',
            sentiment: formProps.initialSentiment || 'neutral',
            detectionPrompt: formProps.backendConfig.detectionPrompt,
            summarizationPromptAddition: formProps.backendConfig.summarizationPromptAddition,
            autoTagFlags: formProps.backendConfig.autoTagFlags,
            detectionTolerance: formProps.backendConfig.detectionTolerance
        }),
        validationSchema: yup.object().shape({
            tagDisplayName: yup.string().required('Display name is required'),
            sentiment: yup.string().oneOf(['positive', 'negative', 'caution', 'neutral']).required('Sentiment is required'),
            detectionPrompt: yup.string(),
            summarizationPromptAddition: yup.string(),
            autoTagFlags: yup.string(),
            detectionTolerance: yup.number().nullable().min(0, 'Must be at least 0').max(1, 'Must be at most 1')
        }),
        handleSubmit: async (values, { props: formProps, setSubmitting }) => {
            formProps.setSubmitState({ submitResult: null, submitMessage: '' });

            const autoTagFlagsArray = values.autoTagFlags
                ? values.autoTagFlags.split(',').map(f => f.trim()).filter(f => f.length > 0)
                : [];

            const request: CreateTagRequest = {
                tagName: formProps.tagName,
                featureFlagId: formProps.featureFlagId,
                tagDisplayName: values.tagDisplayName,
                sentiment: values.sentiment,
                detectionPrompt: values.detectionPrompt || '',
                summarizationPromptAddition: values.summarizationPromptAddition || '',
                autoTagFlags: autoTagFlagsArray,
                detectionTolerance: values.detectionTolerance ? parseFloat(values.detectionTolerance) : null
            };

            try {
                const result = await voicifyApi.updateTag(request);

                if (result.resultType === 'Ok') {
                    formProps.setSubmitState({
                        submitResult: 'success',
                        submitMessage: `Tag "${values.tagDisplayName}" updated successfully.`
                    });
                    setTimeout(() => {
                        formProps.onSuccess();
                    }, 1000);
                } else {
                    const errorMsg = result.errors?.length > 0
                        ? result.errors.join(', ')
                        : 'Failed to update tag. Please check your inputs and try again.';
                    formProps.setSubmitState({
                        submitResult: 'error',
                        submitMessage: errorMsg
                    });
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : 'An unexpected error occurred while updating the tag.';
                formProps.setSubmitState({
                    submitResult: 'error',
                    submitMessage: message
                });
            } finally {
                setSubmitting(false);
            }
        }
    })(InnerForm);

    return (
        <>
            {loadError && <div className={loadWarningStyle}>{loadError}</div>}
            <FormWithFormik
                {...props}
                submitResult={submitState.submitResult}
                submitMessage={submitState.submitMessage}
                setSubmitState={setSubmitState}
                backendConfig={backendConfig}
            />
        </>
    );
};

const formStyle = css`
    margin-top: 8px;
`;

const fieldGroupStyle = css`
    margin-bottom: 16px;
`;

const readOnlyFieldStyle = css`
    margin-bottom: 16px;
`;

const labelStyle = css`
    display: block;
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 0;
    color: ${color_shades_darker};
`;

const labelWithTooltipStyle = css`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`;

const readOnlyValueStyle = css`
    font-family: Muli;
    font-size: 14px;
    color: ${color_shades_darker};
    padding: 12px 16px;
    background-color: #f4f4f6;
    border-radius: 5px;
    border: 1px solid ${color_shades_dark};
`;

const selectStyle = css`
    width: 100%;
    height: 48px;
    padding: 0 16px;
    border: 1px solid ${color_shades_dark};
    border-radius: 5px;
    font-family: Muli;
    font-size: 14px;
    background-color: white;
    appearance: auto;
    cursor: pointer;
    &:focus {
        outline: none;
        border-color: ${color_colors_growth};
    }
`;

const textareaStyle = css`
    width: 100%;
    padding: 12px 16px;
    border: 1px solid ${color_shades_dark};
    border-radius: 5px;
    font-family: Muli;
    font-size: 14px;
    resize: vertical;
    box-sizing: border-box;
    &:focus {
        outline: none;
        border-color: ${color_colors_growth};
    }
`;

const errorStyle = css`
    color: #D8414D;
    font-size: 12px;
    margin-top: 4px;
    font-family: Muli;
`;

const buttonContainerStyle = css`
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
`;

const successMessageStyle = css`
    margin-top: 16px;
    padding: 12px 16px;
    background-color: rgba(88, 189, 153, 0.1);
    border: 1px solid ${color_colors_growth};
    border-radius: 5px;
    color: ${color_colors_growth};
    font-family: Muli;
    font-size: 14px;
`;

const errorMessageStyle = css`
    margin-top: 16px;
    padding: 12px 16px;
    background-color: rgba(216, 65, 77, 0.1);
    border: 1px solid #D8414D;
    border-radius: 5px;
    color: #D8414D;
    font-family: Muli;
    font-size: 14px;
`;

const loadingStyle = css`
    padding: 24px 16px;
    font-family: Muli;
    font-size: 14px;
    color: ${color_shades_darker};
`;

const loadWarningStyle = css`
    margin-bottom: 12px;
    padding: 10px 14px;
    background-color: rgba(255, 193, 7, 0.1);
    border: 1px solid #FFC107;
    border-radius: 5px;
    color: #856404;
    font-family: Muli;
    font-size: 13px;
`;

export default EditTagFormWrapper;
